import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { 
  Users, MessageSquare, FileText, Crown, Trophy, Flame,
  Shield, ShieldCheck, Star, User, Award, ChevronDown
} from "lucide-react";
import { Link } from "react-router-dom";
import { Avatar, Username } from "./UserBadge";
import { useT } from "../i18n";
import { useQuery } from "@tanstack/react-query";

const iconMap = {
  users: Users,
  crown: Crown,
  shield: Shield,
  "shield-check": ShieldCheck,
  star: Star,
  user: User,
  award: Award,
  flame: Flame,
};

const LegendIcon = ({ icon, color, className }) => {
  if (!icon) return <Users className={className} style={{ color }} />;
  if (icon.startsWith("http") || icon.includes("/")) {
    return <img src={icon} alt="Icon" className={`object-contain ${className}`} style={{ width: 14, height: 14 }} />;
  }
  const IconCmp = iconMap[icon] || Users;
  return <IconCmp className={className} style={{ color }} />;
};

export default function ForumStats() {
  const t = useT();
  const [stats, setStats] = useState({});
  const [topMonth, setTopMonth] = useState({ leaders: [] });
  const [newest, setNewest] = useState([]);

  const { data: legendGroups = [] } = useQuery({
    queryKey: ["legendGroups"],
    queryFn: async () => (await api.get("/idg/groups/legend")).data,
    staleTime: 10000,
  });

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/leaderboard/monthly").then((r) => setTopMonth(r.data));
    api.get("/users?sort=recent&limit=8").then((r) => setNewest(r.data));
  }, []);

  return (
    <section className="bg-[#090c12] border border-[#151924] overflow-hidden shadow-2xl transition-all" data-testid="forum-stats">
      {/* 1. Header Bar with Cyber Accent & Live Pulse */}
      <div className="px-4 sm:px-5 py-3.5 bg-[#06080d] border-b border-[#141824] flex items-center justify-between relative overflow-hidden">
        {/* Top ambient highlight line */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[var(--tf-primary)] to-transparent opacity-80" />

        <div className="flex items-center gap-3 relative z-10">
          <div className="w-8 h-8 flex items-center justify-center bg-[var(--tf-primary)]/10 border border-[var(--tf-primary)]/40 text-[var(--tf-primary)] shadow-[0_0_12px_rgba(255,79,0,0.25)]">
            <Trophy className="w-4 h-4 stroke-[2.5]" />
          </div>
          <div>
            <h3 className="font-chakra font-black text-sm uppercase tracking-wider text-white flex items-center gap-2">
              {t("stats.title", "STATISTICI & ACTIVITATE FORUM")}
            </h3>
            <p className="text-[10px] text-zinc-500 font-mono tracking-wider">
              // TELEMETRIE LIVE COMUNITATE
            </p>
          </div>
        </div>

        {/* Live status badge on right */}
        <div className="hidden sm:flex items-center gap-2 bg-black/60 border border-[#181e2b] px-2.5 py-1 text-[10px] font-mono">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_8px_#34d399]" />
          <span className="text-emerald-400 font-bold uppercase tracking-wider">MONITOR ACTIV</span>
        </div>
      </div>

      {/* 2. Interactive Telemetry Stat Tiles Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 divide-x divide-y sm:divide-y-0 divide-[#141824] border-b border-[#141824] bg-[#06080d]/60">
        <StatCell 
          icon={Users} 
          label="MEMBRI" 
          sublabel="Înregistrați"
          value={stats.users} 
          accentColor="#00f0ff"
          badgeBg="bg-cyan-500/10 text-cyan-400 border-cyan-500/30 group-hover:shadow-[0_0_15px_rgba(0,240,255,0.3)]"
          valueColor="group-hover:text-cyan-400"
        />
        <StatCell 
          icon={MessageSquare} 
          label="TOPICURI" 
          sublabel="Discuții"
          value={stats.topics} 
          accentColor="#ff5500"
          badgeBg="bg-orange-500/10 text-orange-400 border-orange-500/30 group-hover:shadow-[0_0_15px_rgba(255,85,0,0.3)]"
          valueColor="group-hover:text-orange-400"
        />
        <StatCell 
          icon={FileText} 
          label="POSTĂRI" 
          sublabel="Răspunsuri"
          value={stats.posts} 
          accentColor="#bf40ff"
          badgeBg="bg-purple-500/10 text-purple-400 border-purple-500/30 group-hover:shadow-[0_0_15px_rgba(191,64,255,0.3)]"
          valueColor="group-hover:text-purple-400"
        />
        <StatCell 
          icon={Crown} 
          label="VIP MEMBERS" 
          sublabel="Beneficii"
          value={stats.vip_count} 
          accentColor="#ffd700"
          badgeBg="bg-amber-500/10 text-amber-400 border-amber-500/30 group-hover:shadow-[0_0_15px_rgba(255,215,0,0.3)]"
          valueColor="text-amber-400 group-hover:text-amber-300"
        />
        <StatCell 
          icon={Flame} 
          label="ONLINE" 
          sublabel="Activ acum"
          value={stats.online_now} 
          accentColor="#10b981"
          badgeBg="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 group-hover:shadow-[0_0_15px_rgba(16,185,129,0.3)]"
          valueColor="text-emerald-400 group-hover:text-emerald-300"
          isLive
        />
        
        {/* Latest Registered User Tile */}
        <div 
          className="p-4 flex flex-col justify-between items-center text-center bg-[#06080d] hover:bg-[#0c101a] transition-all duration-200 group relative cursor-default"
          data-testid="last-registered-member"
        >
          {/* Subtle hover accent line */}
          <div className="absolute bottom-0 inset-x-0 h-0.5 bg-rose-500/0 group-hover:bg-rose-500 transition-all" />

          <div className="w-8 h-8 flex items-center justify-center bg-rose-500/10 text-rose-400 border border-rose-500/30 mb-2 transition-transform group-hover:scale-110 group-hover:shadow-[0_0_15px_rgba(244,63,94,0.3)]">
            <User className="w-4 h-4" />
          </div>

          <div className="min-w-0 max-w-full my-auto py-1">
            {stats.latest_member ? (
              <Username 
                user={stats.latest_member} 
                showBadge={false} 
                className="text-xs sm:text-sm font-chakra font-black tracking-wider truncate block transition-colors group-hover:text-rose-400" 
                hover={true} 
              />
            ) : (
              <span className="text-zinc-600 font-mono text-sm">-</span>
            )}
          </div>

          <div className="text-[10px] font-chakra font-black text-zinc-400 uppercase tracking-widest mt-1">
            ULTIMUL ÎNREGISTRAT
          </div>
        </div>
      </div>

      {/* 3. Legenda Forum with Glowing Rank Badges */}
      {legendGroups.length > 0 && (
        <div className="px-4 sm:px-5 py-4 border-b border-[#141824] bg-[#06080d]" data-testid="forum-legend">
          <div className="text-[10px] font-chakra font-black text-zinc-500 uppercase tracking-widest mb-3 flex items-center gap-2">
            <span>🛡️</span>
            <span>{t("stats.legend", "LEGENDĂ GRUPURI & RANGURI")}</span>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {legendGroups.filter(g => !g.parent_id).map((g) => {
              const children = legendGroups.filter(c => c.parent_id === g.id);
              const groupColor = g.color || "var(--tf-primary)";
              const groupContent = (
                <div
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-chakra font-bold transition-all duration-200 border relative z-10 cursor-pointer shadow-sm group/btn hover:scale-105"
                  style={{
                    color: groupColor,
                    backgroundColor: "#080b12",
                    borderColor: `${groupColor}40`,
                    boxShadow: `inset 0 0 10px ${groupColor}10`
                  }}
                  title={g.description}
                >
                  <LegendIcon icon={g.label_icon} color={groupColor} className="w-3.5 h-3.5 shrink-0 transition-transform group-hover/btn:scale-110" />
                  <span className="tracking-wide">{g.name}</span>
                  {children.length > 0 && <ChevronDown className="w-3 h-3 opacity-60 ml-1 group-hover/btn:translate-y-0.5 transition-transform" />}
                </div>
              );

              if (children.length > 0) {
                return (
                  <div key={g.id} className="relative group/dropdown">
                    <Link to={`/members?group=${g.id}`} className="block" translate="no">
                      {groupContent}
                    </Link>
                    <div className="absolute top-full left-0 mt-1 hidden group-hover/dropdown:flex flex-col gap-1 z-50 p-2 bg-[#090c12] border border-[#181e2b] min-w-[220px] shadow-2xl animate-in fade-in slide-in-from-top-2 backdrop-blur-xl">
                      {children.map(c => (
                        <Link
                          key={c.id}
                          to={`/members?group=${c.id}`}
                          className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-[#0e131d] border border-transparent hover:border-[#181e2b] transition-colors text-xs font-chakra font-bold"
                          style={{ color: c.color || groupColor }}
                          title={c.description}
                          translate="no"
                        >
                          <LegendIcon icon={c.label_icon} color={c.color} className="w-3.5 h-3.5 shrink-0" />
                          <span>{c.name}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }

              return (
                <Link key={g.id} to={`/members?group=${g.id}`} className="block" translate="no">
                   {groupContent}
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* 4. Top Monthly Activity & Newest Members */}
      <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#141824] bg-[#06080d]/40">
        {/* Top Active Column */}
        <div className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3.5">
            <h4 className="text-[11px] font-chakra font-black text-amber-400 uppercase tracking-widest flex items-center gap-2">
              <span className="p-1 bg-amber-500/10 border border-amber-500/30">🏆</span>
              <span>{t("stats.month_top", "TOP LUNA ACEASTA")}</span>
            </h4>
            <span className="text-[9px] font-mono text-zinc-500 bg-black/60 border border-[#141824] px-2 py-0.5 uppercase tracking-wider">
              PUNCTE FORUM
            </span>
          </div>

          <div className="space-y-2">
            {(topMonth.leaders || []).slice(0, 5).map((u, i) => {
              const isFirst = i === 0;
              const isSecond = i === 1;
              const isThird = i === 2;
              
              const borderAccent = isFirst 
                ? "border-l-2 border-l-amber-400 bg-gradient-to-r from-amber-500/10 via-[#06080d] to-[#06080d]" 
                : isSecond 
                  ? "border-l-2 border-l-slate-300 bg-gradient-to-r from-slate-400/10 via-[#06080d] to-[#06080d]" 
                  : isThird 
                    ? "border-l-2 border-l-amber-600 bg-gradient-to-r from-amber-700/10 via-[#06080d] to-[#06080d]" 
                    : "bg-[#06080d]";

              const medalBadge = isFirst ? "🥇" : isSecond ? "🥈" : isThird ? "🥉" : `#${i + 1}`;
              const ptsBadgeColor = isFirst 
                ? "text-amber-400 bg-amber-500/10 border-amber-500/30" 
                : isSecond 
                  ? "text-slate-300 bg-slate-400/10 border-slate-400/30" 
                  : isThird 
                    ? "text-amber-600 bg-amber-600/10 border-amber-600/30" 
                    : "text-zinc-400 bg-black/60 border-[#181e2b]";

              return (
                <Link 
                  key={u.id} 
                  to={`/u/${u.username}`} 
                  className={`flex items-center gap-3 p-2.5 border border-[#141824] hover:border-[var(--tf-primary)]/50 transition-all duration-200 group hover:translate-x-1 shadow-sm ${borderAccent}`}
                >
                  <div className="w-6 text-center font-chakra font-black text-sm shrink-0">
                    {medalBadge}
                  </div>
                  
                  <div className="shrink-0 relative">
                    <Avatar user={u} size={32} hover />
                  </div>

                  <div className="flex-1 min-w-0">
                    <Username user={u} className="text-xs font-chakra font-bold tracking-wide truncate block" />
                  </div>

                  <span className={`ml-auto font-mono text-xs font-bold px-2.5 py-1 border shrink-0 transition-colors ${ptsBadgeColor}`}>
                    {u.monthly_points} pts
                  </span>
                </Link>
              );
            })}
            {!topMonth.leaders?.length && (
              <p className="text-xs font-mono text-zinc-500 text-center py-6">
                {t("stats.no_activity", "Nicio activitate înregistrată.")}
              </p>
            )}
          </div>
        </div>

        {/* Newest Members Column */}
        <div className="p-4 sm:p-5">
          <div className="flex items-center justify-between mb-3.5">
            <h4 className="text-[11px] font-chakra font-black text-cyan-400 uppercase tracking-widest flex items-center gap-2">
              <span className="p-1 bg-cyan-500/10 border border-cyan-500/30">✨</span>
              <span>{t("stats.newest_members", "CEI MAI NOI MEMBRI")}</span>
            </h4>
            <span className="text-[9px] font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 uppercase tracking-wider">
              BINE AȚI VENIT!
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {newest.map((u) => (
              <Link 
                key={u.id} 
                to={`/u/${u.username}`} 
                className="flex items-center gap-2.5 bg-[#06080d] hover:bg-[#0c101a] border border-[#141824] hover:border-[var(--tf-primary)]/60 p-2 transition-all duration-200 group shadow-sm hover:scale-[1.02] relative overflow-hidden"
              >
                {/* Subtle top hover line */}
                <div className="absolute top-0 inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-[var(--tf-primary)] to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                <div className="shrink-0">
                  <Avatar user={u} size={26} hover />
                </div>
                
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-chakra font-bold text-zinc-200 group-hover:text-white truncate block transition-colors">
                    {u.username}
                  </span>
                  <span className="text-[9px] font-mono text-zinc-500 truncate block">
                    ID: #{u.id?.slice(0, 4) || "001"}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function StatCell({ icon: Icon, label, sublabel, value, accentColor, badgeBg, valueColor, isLive }) {
  return (
    <div className="p-4 text-center bg-[#06080d] hover:bg-[#0c101a] transition-all duration-200 group relative flex flex-col justify-between items-center cursor-default">
      {/* Bottom accent glow line on hover */}
      <div 
        className="absolute bottom-0 inset-x-0 h-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
        style={{ backgroundColor: accentColor }}
      />

      {/* Top Icon Badge */}
      <div className={`w-8 h-8 flex items-center justify-center border mb-2 transition-transform duration-200 group-hover:scale-110 ${badgeBg}`}>
        <Icon className="w-4 h-4" />
      </div>

      {/* Big Digits */}
      <div className={`font-mono text-2xl font-black tracking-tight text-white transition-colors duration-200 my-auto ${valueColor || ''}`}>
        {value ?? 0}
      </div>

      {/* Label and Sublabel */}
      <div className="mt-1">
        <div 
          className="text-[10px] font-chakra font-black uppercase tracking-wider transition-colors"
          style={{ color: accentColor }}
        >
          {label}
        </div>
        {sublabel && (
          <div className="text-[9px] font-mono text-zinc-500">
            {sublabel}
          </div>
        )}
      </div>
    </div>
  );
}
